import Api from '@/common/api';
import { TransStatement } from '@/common/api/model';
import { ActionContext } from 'vuex';
import * as env from '@/common/environment';

import { TRANSACTION_STATUS_ENUM, TRANSACTION_STATUS_NAME_ENUM, TransactionStatus, TransactionStatusName, UPDATED_STATUS_WARNING_VARIANTS, UpdatedStatusWarningVariant, getTransactionWarning, isTransactionSupportRemedy, isTxStatusPending, isTxStatusSuccessful, isTxStatusUnknown } from '@/common/transaction-utils';
import Vue from 'vue';
import { i18n } from '@/plugins/i18n';
import { event } from 'vue-gtag';
import { GA_EVENT_CUSTOM_DIMENSION_ENUM, GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import { analytics, GENERAL_EVENTS } from '@/common/logs';

// TODO: move types to separate file
interface TransactionState {
  selectedTransactions: SelectedTransaction[];
  updatedTransactions: { [key: string]: TransactionStatus };
  isCheckingStatusForTransaction: boolean;
  showTransactionUpdatedWarning: boolean;
}

const state: TransactionState = {
  // maybe save selected transactions by transaction type + external transaction type
  selectedTransactions: [],
  updatedTransactions: {},
  isCheckingStatusForTransaction: false,
  showTransactionUpdatedWarning: false,
};

export interface SelectedTransaction extends TransStatement {
  txPage: number;
}

// TODO: keep mutations, state, getters, action separately in files
const mutations = {
  addSelectedTransactions (state: TransactionState, transactions: SelectedTransaction[]) {
    transactions.forEach(transaction => {
      if (!state.selectedTransactions.some(tx => tx.tradeId === transaction.tradeId)) {
        state.selectedTransactions.push(transaction);
      } else {
        state.selectedTransactions = state.selectedTransactions.filter(tx => tx.tradeId !== transaction.tradeId);
      }
    });
  },
  resetSelectedTransactions (state: TransactionState) {
    state.selectedTransactions = [];
  },
  setUpdatedTransaction (state: TransactionState, { tradeId, status }: TransStatement) {
    Vue.set(state.updatedTransactions, tradeId, status);
  },
  setSelectedTransactionStatus (state: TransactionState, { tradeId, status }: { tradeId: string, status: TransactionStatus }) {
    const transaction = state.selectedTransactions.find(tx => tx.tradeId === tradeId);
    if (transaction) {
      transaction.status = status;
    }
  },
  setIsCheckingStatusForTransaction (state: TransactionState, status: boolean) {
    state.isCheckingStatusForTransaction = status;
  },
  setShowTransactionUpdatedWarning (state: TransactionState, show: boolean) {
    state.showTransactionUpdatedWarning = show;
  },
};

const actions = {
  async checkTransactionStatus ({ commit, getters }: ActionContext<TransactionState, unknown>) {
    // Using a getter to get transactions available for update
    const transactionsForUpdate = getters.selectedTransactionAvailableForUpdate as TransStatement[];
    const [transactionForUpdate, ...restTransactions] = transactionsForUpdate;

    if (!transactionForUpdate) {
      return;
    }

    const { payChId, payChTxId, tradeId, status } = transactionForUpdate;

    const data = { payCh: payChId, chTxId: payChTxId };
    const dataToLog = { ...data, tradeId };

    const dataStringified = JSON.stringify(dataToLog);

    try {
      commit('setIsCheckingStatusForTransaction', true);

      const result = await Api.refreshTransactionStatus(data);

      const errorCode = result.data.errorCode;

      if (result.data.isMatch !== 1) {
        event(GA_EVENT_NAME_ENUM.REMEDY_TRANSACTION_RUN_MATCH_FAILED, {
          [GA_EVENT_CUSTOM_DIMENSION_ENUM.REMEDY_DATA]: dataStringified,
          [GA_EVENT_CUSTOM_DIMENSION_ENUM.ERROR_CODE]: errorCode,
        });
        analytics.track({
          name: GENERAL_EVENTS.CONNECT_REQUEST_TRANSACTION_REMEDY_MATCH_FAILED,
          selection: tradeId,
          metadata: {
            transaction: transactionForUpdate,
            errorCode,
          },
        });
        throw new Error('Didn\'t match transaction');
      }

      const updatedStatus = (isTxStatusUnknown(result.data.finalStatus) ? status : result.data.finalStatus) as TransactionStatus;

      if (!updatedStatus || !TRANSACTION_STATUS_NAME_ENUM[updatedStatus]) {
        event(GA_EVENT_NAME_ENUM.REMEDY_TRANSACTION_RUN_MATCH_FAILED, {
          [GA_EVENT_CUSTOM_DIMENSION_ENUM.ERROR_CODE]: errorCode,
          [GA_EVENT_CUSTOM_DIMENSION_ENUM.REMEDY_DATA]: dataStringified,
        });
        analytics.track({
          name: GENERAL_EVENTS.CONNECT_REQUEST_TRANSACTION_REMEDY_MATCH_FAILED,
          selection: tradeId,
          metadata: {
            transaction: transactionForUpdate,
            errorCode,
          },
        });
        throw new Error('Unknown status');
      }

      const updatedStatusName = TRANSACTION_STATUS_NAME_ENUM[updatedStatus].toLowerCase();

      event(GA_EVENT_NAME_ENUM.REMEDY_TRANSACTION_NEW_STATUS_ + updatedStatusName, {
        [GA_EVENT_CUSTOM_DIMENSION_ENUM.REMEDY_DATA]: dataStringified,
      });

      analytics.track({
        name: GENERAL_EVENTS.CONNECT_REQUEST_TRANSACTION_REMEDY_STATUS_CHANGED,
        selection: tradeId,
        metadata: {
          transaction: transactionForUpdate,
          updatedStatusName,
        },
      });

      commit('setUpdatedTransaction', { tradeId, status: updatedStatus });
      commit('setSelectedTransactionStatus', { tradeId, status: updatedStatus });

      restTransactions.forEach(tx => {
        commit('setUpdatedTransaction', { tradeId: tx.tradeId, status: tx.status });
      });
    } catch (e) {
      const errorMessage = (e as Error).toString() || (e as Error)?.message || null;

      console.error(e);
      event(GA_EVENT_NAME_ENUM.REMEDY_TRANSACTION_RUN_FAILED, {
        [GA_EVENT_CUSTOM_DIMENSION_ENUM.REMEDY_DATA]: dataStringified,
        [GA_EVENT_CUSTOM_DIMENSION_ENUM.ERROR_MESSAGE]: errorMessage,
      });
      // In case of error, keep all transactions updated with old status
      transactionsForUpdate.forEach(tx => {
        commit('setUpdatedTransaction', { tradeId: tx.tradeId, status: tx.status });
      });
    } finally {
      event(GA_EVENT_NAME_ENUM.REMEDY_TRANSACTION_RUN_FINISHED);
      commit('setIsCheckingStatusForTransaction', false);
      commit('setShowTransactionUpdatedWarning', true);
    }

    return state.updatedTransactions;
  },
};

interface TransactionGetters {
  shouldCheckStatusForDeposit: boolean;
  isTransactionStatusUpdatedSuccessfully: boolean;
  transactionStatusFlags: {
    isCheckingStatusForTransaction: boolean;
    showTransactionUpdatedWarning: boolean;
    shouldCheckStatusForDeposit: boolean;
    isTransactionStatusUpdatedSuccessfully: boolean;
  };
  updatedTransactionsLength: number;
  allSelectedTransactionsUpdated: boolean;
  hasOnlyPendingUpdatedTransactions: boolean;
  hasOnlySuccessfulUpdatedTransactions: boolean;
  selectedTransactionAvailableForUpdate: TransStatement[];
  updatedTransactionsNotificationText: string;
  updatedTransactionsNotificationType: UpdatedStatusWarningVariant;
  updatedTransactionsNotification: { text: string, type: UpdatedStatusWarningVariant };
  getUpdatedTransactionStatus: (tx: TransStatement) => TransactionStatusName | null;
}

const getters = {
  shouldCheckStatusForDeposit: (_: TransactionState, getters: TransactionGetters): boolean => {
    const features = env.get('features');

    if (features && features.remedy === false) {
      return false;
    }

    return getters.selectedTransactionAvailableForUpdate.length > 0;
  },

  isTransactionStatusUpdatedSuccessfully: (state: TransactionState): boolean | null => {
    if (Object.keys(state.updatedTransactions).length === 0) {
      return null;
    }

    return Object.values(state.updatedTransactions).some(
      status => !(
        isTxStatusUnknown(status) &&
        isTxStatusPending(status)
      )
    );
  },

  transactionStatusFlags: (
    state: TransactionState,
    getters: TransactionGetters
  ): {
    isCheckingStatusForTransaction: boolean;
    showTransactionUpdatedWarning: boolean;
    shouldCheckStatusForDeposit: boolean;
    isTransactionStatusUpdatedSuccessfully: boolean;
  } => {
    return {
      isCheckingStatusForTransaction: state.isCheckingStatusForTransaction,
      showTransactionUpdatedWarning: state.showTransactionUpdatedWarning,
      shouldCheckStatusForDeposit: getters.shouldCheckStatusForDeposit,
      isTransactionStatusUpdatedSuccessfully: getters.isTransactionStatusUpdatedSuccessfully,
    };
  },

  updatedTransactionsLength: (state: TransactionState) => {
    return Object.keys(state.updatedTransactions).length;
  },

  allSelectedTransactionsUpdated: (state: TransactionState): boolean => {
    return state.selectedTransactions.length > 0 && state.selectedTransactions.every(tx => state.updatedTransactions[tx.tradeId] !== undefined);
  },

  hasOnlyPendingUpdatedTransactions: (state: TransactionState, getters: TransactionGetters): boolean => {
    return getters.allSelectedTransactionsUpdated && state.selectedTransactions.every(tx => isTxStatusPending(state.updatedTransactions[tx.tradeId]));
  },

  hasOnlySuccessfulUpdatedTransactions: (state: TransactionState, getters: TransactionGetters): boolean => {
    return getters.allSelectedTransactionsUpdated && state.selectedTransactions.every(tx => isTxStatusSuccessful(state.updatedTransactions[tx.tradeId]));
  },

  selectedTransactionAvailableForUpdate (state: TransactionState, _getters: TransactionGetters, _rootState: any, rootGetters: any): TransStatement[] {
    const transactionsAvailableForUpdate = state.selectedTransactions.filter(
      transaction =>
        // if transaction supports remedy
        isTransactionSupportRemedy(transaction) &&
        // without warnings
        !getTransactionWarning({ transaction, chatWarnings: rootGetters['chatWarning/chatWarnings'] }).show &&
        // not checked previously
        !state.updatedTransactions[transaction.tradeId]
    );

    // We can check only 1 transaction per 30 seconds, so select first one
    return transactionsAvailableForUpdate;
  },

  getUpdatedTransactionStatus: (state: TransactionState) => {
    const { updatedTransactions } = state;

    return (tx: TransStatement): TransactionStatusName | null => {
      if (!updatedTransactions[tx.tradeId]) {
        return null;
      }

      const tradeId = tx.tradeId;
      const transactionStatus = updatedTransactions[tradeId];

      if (isTxStatusPending(transactionStatus)) {
        return null;
      }

      return transactionStatus ? TRANSACTION_STATUS_NAME_ENUM[transactionStatus] : null;
    };
  },

  updatedTransactionsNotificationText: (state: TransactionState, getters: TransactionGetters): string => {
    let statusCounts = {
      Pending: 0,
      Successful: 0,
      Failed: 0,
    };

    Object.values(state.updatedTransactions).forEach((status) => {
      // Taking first pending status since we don't want to show "Unknown" here
      const statusToUpdate = isTxStatusUnknown(status) ? TRANSACTION_STATUS_ENUM.PENDING[0] : status;
      const statusString = TRANSACTION_STATUS_NAME_ENUM[statusToUpdate];
      if (statusString) {
        statusCounts[statusString as keyof typeof statusCounts] += 1;
      }
    });

    const outputParts = Object.entries(statusCounts).map(([statusKey, count]) => {
      if (count === 0) return null;

      const ratio = `${count}/${getters.updatedTransactionsLength}`;
      const statusPart = i18n.tc(`page.issue.transaction.remedy.${statusKey.toLowerCase()}`, count, { ratio }).toString();

      return statusPart;
    }).filter(part => part);

    return outputParts.join(', ');
  },

  updatedTransactionsNotificationType: (_: TransactionState, getters: TransactionGetters): UpdatedStatusWarningVariant => {
    const allTransactionsUpdatedToPending = Object.values(
      state.updatedTransactions
    ).every(isTxStatusPending);

    if (allTransactionsUpdatedToPending) {
      return UPDATED_STATUS_WARNING_VARIANTS.PROMINENT;
    }

    const allTransactionsUpdatedToSuccessful = Object.values(
      state.updatedTransactions
    ).every(isTxStatusSuccessful);

    if (allTransactionsUpdatedToSuccessful) {
      return UPDATED_STATUS_WARNING_VARIANTS.SUCCESSFUL;
    }

    return UPDATED_STATUS_WARNING_VARIANTS.WARNING;
  },

  updatedTransactionsNotification: (_: TransactionState, getters: TransactionGetters): { text: string, type: UpdatedStatusWarningVariant } => {
    return {
      text: getters.updatedTransactionsNotificationText,
      type: getters.updatedTransactionsNotificationType,
    };
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
